import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Camera,
  Image as ImageIcon,
  Keyboard,
  ScanLine,
  RefreshCw,
  Zap,
  ZapOff,
} from "lucide-react";
import { toast } from "sonner";
import useQrScanner from "@/lib/useQrScanner";

function ScannerPlaceholder({ status, error, onStart }) {
  if (status === "scanning") return null;
  const isError = status === "error";
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-slate-200 p-6 gap-3">
      {!isError ? (
        <ScanLine className="h-10 w-10 opacity-70" />
      ) : (
        <Camera className="h-10 w-10 text-red-400" />
      )}
      {status === "starting" && <p className="text-sm">Starting camera…</p>}
      {status === "idle" && (
        <>
          <p className="text-sm max-w-xs">
            Hold the QR sticker on the tool in the centre of the frame. The
            scan starts as soon as you tap below.
          </p>
          <Button
            onClick={onStart}
            data-testid="scan-start-btn"
            className="touch-btn h-14 px-6 text-base font-bold bg-white text-slate-900 hover:bg-slate-100 rounded-xl gap-2"
          >
            <Camera className="h-5 w-5" /> Tap to start camera
          </Button>
        </>
      )}
      {isError && (
        <>
          <p
            className="text-sm text-red-200 max-w-xs leading-relaxed"
            data-testid="scan-error"
          >
            {error}
          </p>
          <Button
            onClick={onStart}
            data-testid="scan-retry-btn"
            variant="outline"
            className="touch-btn h-12 px-5 bg-transparent border-white/40 text-white hover:bg-white/10 gap-2"
          >
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        </>
      )}
    </div>
  );
}

function TorchButton({ supported, torchOn, onToggle }) {
  if (!supported) return null;
  return (
    <button
      onClick={onToggle}
      data-testid="scan-torch-btn"
      aria-label={torchOn ? "Turn off torch" : "Turn on torch"}
      className="absolute top-3 right-3 h-11 w-11 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur-sm"
    >
      {torchOn ? <ZapOff className="h-5 w-5" /> : <Zap className="h-5 w-5" />}
    </button>
  );
}

export default function Scan() {
  const navigate = useNavigate();

  const handleCode = useCallback(
    async (code) => {
      const trimmed = (code || "").trim();
      try {
        const { data } = await api.get(
          `/tools/by-code/${encodeURIComponent(trimmed)}`
        );
        navigate(`/worker/timer/${encodeURIComponent(data.code)}`, { replace: true });
      } catch (err) {
        console.error("Tool lookup failed:", err);
        toast.error(`Tool code "${trimmed}" not found`);
        navigate("/worker/manual", { replace: true });
      }
    },
    [navigate]
  );

  const {
    regionId,
    status,
    error,
    torchOn,
    torchSupported,
    start,
    toggleTorch,
    scanFile,
  } = useQrScanner({ onDecoded: handleCode });

  const fileRef = useRef(null);
  const [scanningPhoto, setScanningPhoto] = useState(false);

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-pick of same file
    if (!file) return;
    setScanningPhoto(true);
    try {
      const decoded = await scanFile(file);
      await handleCode(decoded);
    } catch (err) {
      console.error("Photo decode failed:", err);
      toast.error("No QR code found in that photo. Try again or enter the code.");
    } finally {
      setScanningPhoto(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="scan-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          Step 2
        </p>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">
          Scan tool QR code
        </h1>
        <p className="text-slate-600 text-sm mt-1">
          Allow camera access when prompted, then point the lens at the QR
          sticker on the tool.
        </p>
      </div>

      <Card className="p-3 border-slate-200 overflow-hidden">
        <div
          id={regionId}
          className="relative rounded-lg overflow-hidden bg-slate-900 min-h-[320px] flex items-center justify-center"
          data-testid="qr-camera-region"
        >
          <ScannerPlaceholder status={status} error={error} onStart={start} />
          <TorchButton
            supported={torchSupported}
            torchOn={torchOn}
            onToggle={toggleTorch}
          />
        </div>
      </Card>

      {/* Hidden native-camera file picker — every phone supports this even
          when getUserMedia / live preview is blocked. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPhotoPicked}
        data-testid="scan-photo-input"
      />

      <Button
        onClick={() => fileRef.current?.click()}
        disabled={scanningPhoto}
        variant="outline"
        className="w-full touch-btn h-16 border-2 border-slate-300 text-slate-800 text-lg font-bold rounded-xl gap-3"
        data-testid="scan-photo-btn"
      >
        <ImageIcon className="h-6 w-6" />
        {scanningPhoto ? "Reading photo…" : "Take a photo of the QR code"}
      </Button>

      <Button
        variant="outline"
        onClick={() => navigate("/worker/manual")}
        className="w-full touch-btn h-16 border-2 border-slate-900 text-slate-900 text-lg font-bold rounded-xl gap-3"
        data-testid="scan-manual-fallback-btn"
      >
        <Keyboard className="h-6 w-6" /> Enter code manually
      </Button>

      <p className="text-xs text-slate-500 text-center">
        Tip: if the live camera won't start on your phone, use{" "}
        <strong>Take a photo</strong> instead — it works on virtually every device.
      </p>
    </div>
  );
}
